import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Space, Tag, Tooltip, Typography } from 'antd';
import styled from 'styled-components';
import { getDownloadUrl, getFullName } from '@/utils';
import { selectorPermissions } from '@/stores/auth';
import { DownloadSvg, PlusSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';

const { Text } = Typography;

const Wrapper = styled.div`
  padding: 0 24px 24px 24px;
  background-color: var(--colorBgContainer);
  border-radius: var(--borderRadius);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-block: 16px;
  padding-right: 8px;

  .title {
    font-size: 16px;
    font-weight: 500;
    margin-right: auto;
  }
`;

const columns = [
  {
    title: 'Primary Name',
    key: 'primary',
    render: (_, record) => (
      <Space direction="vertical" size={2}>
        <Link to={`/parent/${record.parentId}`}>{getFullName(record.primary.name)}</Link>
        {record.inactiveDate && (
          <Text type="danger" italic>
            Inactive
          </Text>
        )}
        {record.matchedDate && <Tag color="green">Matched</Tag>}
        {record.placementCapacityDate && <Tag color="warning">{record.placementCapacityNote}</Tag>}
        {record.adoptedWithinPast12Months === true && <Tag color="blue">Adopted within past 12 months</Tag>}
      </Space>
    ),
  },
  {
    title: 'Significant Other',
    dataIndex: 'significantOther',
    render: (value) => getFullName(value?.name),
  },
  {
    title: 'Adoption Liaison & County',
    dataIndex: 'county',
    render: (county, record) => (
      <>
        <div>{getFullName(record.adoptionLiaison?.name)}</div>
        <div>
          {county.name} <i>({county.region.name})</i>
        </div>
      </>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    render: (_, record) => (
      <>
        <div>{record.homeStudy.status.name}</div>
        <i>{record.adoptionRecommendation.recommended ? 'Recommended' : 'Not Recommended'}</i>
      </>
    ),
  },
  {
    title: 'Family Status',
    key: 'familyStatus',
    render: (_, record) => record.homeStudy.type.name,
  },
];

const SearchResults = ({ searchId, data, loading }) => {
  const permissions = useSelector(selectorPermissions);

  return (
    <Wrapper id="search-results">
      <Toolbar>
        <div className="title">Search Results</div>
        {permissions.writeParent && (
          <Button type="primary" icon={<PlusSvg />} to="/parent/new">
            Add
          </Button>
        )}
        <Tooltip title="Download Results">
          <Button
            type="link"
            icon={<DownloadSvg fontSize="18" />}
            disabled={!data?.count}
            href={getDownloadUrl(`/search/parents/${searchId}/download/excel`)}
          />
        </Tooltip>
      </Toolbar>

      <Table columns={columns} dataSource={data?.data} rowKey="parentId" loading={loading} pageInfo={data} />
    </Wrapper>
  );
};

export default memo(SearchResults);
